/*
 * Builds page: pure model, no React. The API filters derived from the URL search, the live-dot and provider-lane copy,
 * the environments offered to the filter bar and the total shown on the "All" chip.
 */
import type { Environment, Platform } from '@hangar/shared';
import type { BuildFilters } from '@/api/endpoints';
import type { QueueSnapshot } from '@/api/schemas';
import type { Tone } from '@/components/badges';
import type { StreamStatus } from '@/features/live/liveModel';
import type { BuildsSearch } from '@/router';
import { statusesOf } from './buildsFiltersModel';

/** Page size of the builds list (cursor pagination). */
export const PAGE_SIZE = 25;

/**
 * The `ref` filter (`app#number`) from the search box: a full reference goes as typed, bare digits are completed with the
 * selected app, anything else is no reference (the box then only narrows by the other filters).
 */
export function referenceOf(search: Pick<BuildsSearch, 'q' | 'app'>): string | undefined {
  const q = search.q?.trim();
  if (!q) return undefined;
  if (q.includes('#')) return q;
  return /^\d+$/.test(q) && search.app ? `${search.app}#${q}` : undefined;
}

export function buildFilters(projectId: string | undefined, search: BuildsSearch): BuildFilters {
  return {
    projectId,
    app: search.app,
    environment: search.environment,
    platform: search.platform as Platform | undefined,
    status: statusesOf(search.status),
    branch: search.branch,
    ref: referenceOf(search),
    limit: PAGE_SIZE,
  };
}

/** Environments the filter offers: the live ones, in the project's order. */
export const activeEnvironments = (environments: Environment[]): Environment[] => environments.filter((e) => !e.archivedAt).sort((a, b) => a.sortOrder - b.sortOrder);

/** Colour of the live dot: green while connected, amber while reconnecting, grey otherwise. */
export function liveTone(live: StreamStatus): Tone {
  if (live === 'connected') return 'running';
  return live === 'reconnecting' ? 'awaiting' : 'subtle';
}

/** Text of the live badge: "live" when connected, else the status itself. */
export const liveText = (live: StreamStatus): string => (live === 'connected' ? 'live' : live);

type Lane = QueueSnapshot['lanes'][number];

/** A provider lane is full when every build slot is taken; otherwise it says how many are free. */
export function laneState(lane: Pick<Lane, 'inUse' | 'concurrencyLimit'>): { busy: boolean; label: string } {
  const busy = lane.inUse >= lane.concurrencyLimit;
  return { busy, label: busy ? 'full' : `${lane.concurrencyLimit - lane.inUse} free` };
}

/** The "All" chip shows the total only when it is known: nothing filtered, rows loaded and no further page. */
export const allTotal = (filtered: boolean, rows: unknown[] | undefined, hasNextPage: boolean): number | undefined => (!filtered && rows && !hasNextPage ? rows.length : undefined);
